import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { ru } from 'date-fns/locale/ru';
import { isValid } from 'date-fns/isValid';
import { format } from 'date-fns';
import { addSeconds } from 'date-fns/addSeconds';
import { addDays } from 'date-fns/addDays';
import { parse } from 'date-fns/parse';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { useLatest } from 'react-use';
import { useField } from 'formik';
import type { FieldHookConfig } from 'formik';
import DateTimePicker, { registerLocale } from 'react-datepicker';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import moment from 'moment';

import 'react-datepicker/dist/react-datepicker.css';
import './DateTimeInput.scss';

import { dateUtils } from 'modules/core/utils';
import { Button } from 'modules/core/components/buttons';
import { FormContext, isRequiredField } from '../utils';
import DateTimeMask from './DateTimeMask';
import type { SimpleMaskInputProps } from '../SimpleMaskInput';

registerLocale('ru', ru);

const isoFormatPattern = 'Z';

// Стабильная ссылка для дефолта onChange (Rule 3 skill remove-defaultprops)
const noopOnChange = () => { };

// Границы выбора дат и списка годов в дропдауне выбора года — react-datepicker
// строит список годов именно по minDate/maxDate: 2019 .. текущий год + 1.
const defaultMinDate = new Date(2019, 0, 1);
const defaultMaxDate = () => new Date(new Date().getFullYear() + 1, 11, 31);

/** Опции конвертации часового пояса для valueToDateObject/valueToDateString. */
interface DateTimeZoneOptions {
  timeZone?: string;
  timeZoneOffset?: number;
}

const valueToDateObject = (value: any, fromFormat: string, options: DateTimeZoneOptions = {}): any => {
  if (typeof value === 'object') {
    return value;
  }

  const { timeZone, timeZoneOffset } = options;

  /*
  Далее экспериментальный код для перехода на date-fns 3.x.x
  Не получилось добиться стабильной работы

  const myParse = (value !== undefined) ? parse(value, fromFormat, new Date()) : new Date("nothing");

  const date = (fromFormat === isoFormatPattern)
    ? new Date(value)
    : myParse;

  */

  const convertDate = (v: any) => {
    return typeof v === 'number' ? moment.unix(v).toDate() : parse(v, fromFormat, new Date());
  }

  const date = ((fromFormat === isoFormatPattern) || (!value))
    ? new Date(value)
    : convertDate(value)

  try {
    return (timeZone || timeZoneOffset)
      ? dateUtils.utcToZonedTime(date, timeZone, timeZoneOffset)
      : date;
  } catch {
    return dateUtils.utcToZonedTime(date, 'Europe/Moscow');
  }
};

const valueToDateString = (value: any, toFormat: string, options: DateTimeZoneOptions = {}): any => {
  if (!isValid(value)) {
    return value;
  }

  const { timeZone, timeZoneOffset } = options;

  let date = value;

  try {
    if (timeZone || timeZoneOffset) { date = dateUtils.utcFromZonedTime(value, timeZone, timeZoneOffset); }
  } catch {
    if (timeZone || timeZoneOffset) { date = dateUtils.utcFromZonedTime(value, 'Europe/Moscow', timeZoneOffset); }
  }

  return (toFormat === isoFormatPattern)
    ? date.toISOString()
    : format(date, toFormat);
};

/**
 * Пропсы Formik-инпута даты/времени на react-datepicker (customInput — DateTimeMask).
 *
 * `onComplete`/`onManualChange`/`maskPattern` устанавливаются компонентом внутри
 * (в inputProps) и от потребителя не принимаются; `value` живёт в formik
 * (`field.value` через useField).
 */
export interface DateTimeInputProps extends Omit<SimpleMaskInputProps,
  'value' | 'onChange' | 'onComplete' | 'onManualChange' | 'maskPattern'> {
  /** Имя поля формы (formik). */
  name: string;
  /**
   * Формат отображаемой даты.
   * @default 'dd.MM.yyyy HH:mm'
   * @see https://date-fns.org/v2.16.1/docs/format
   */
  displayFormat?: string;
  /**
   * Формат передаваемой на сервер даты
   *
   * Метод `date-fns[-tz]/format` не поддерживает форматирование в строку,
   * аналогичную результату метода `Date.toISOString()`.
   * Для такого случая нужно указать паттерн `'Z'` в строке формата,
   * что заставит компонент напрямую использовать `Date.toISOString()`
   * для получения значения, отправляемого на сервер
   * @default 't' - unix timestamp
   * @see https://date-fns.org/v2.16.1/docs/format
   */
  serverFormat?: string;
  /**
   * Показывать поле ввода времени или нет
   * @default true
   */
  showTime?: boolean;
  /**
   * Используемый часовой пояс в виде строки из базы данных часовых поясов IANA
   * @example 'Europe/Moscow'
   * @example 'UTC'
   */
  timeZone?: string;
  /**
   * Смещение часового пояса относительно часового пояса UTC в минутах
   * @example 180 - смещение для часового пояса Europe/Moscow
   */
  timeZoneOffset?: number;
  /**
   * Показывать или нет кнопку очистки
   */
  isClearable?: boolean;
  /** Значение поля (рабочий канал — formik через useField). */
  value?: string | number | Date;
  /**
   * Колбэк после записи значения в форму: синтетический event
   * `{ target: { name, value, rawValue } }` (rawValue — значение в serverFormat).
   */
  onChange?: (event: { target: { name?: string; value: any; rawValue?: any } }) => void;
  /** Отнять 1 секунду от устанавливаемой даты (нужно для удобной работы с датой "до"). */
  minusOneSecond?: boolean;
  /** @default true */
  shouldCloseOnSelect?: boolean;
  /**
   * Минимальная выбираемая дата; ограничивает и список годов в дропдауне выбора года
   * @default 01.01.2019
   */
  minDate?: Date;
  /**
   * Максимальная выбираемая дата; ограничивает и список годов в дропдауне выбора года
   * @default 31.12 текущего года + 1
   */
  maxDate?: Date;
  /** Formik-валидация поля (прокидывается в useField). */
  validate?: (value: any) => any;
}

const DateTimeInput = forwardRef<HTMLElement, DateTimeInputProps>((props, ref) => {
  const picker = useRef<any>(null);
  // Добавляем ref для отслеживания ручного ввода
  const isManualInputRef = useRef(false);
  const {
    disabled,
    readOnly,
    submitCount,
    validationSchema,
    validateField,
  } = useContext(FormContext) as {
    disabled?: boolean;
    readOnly?: boolean;
    submitCount: number;
    validationSchema?: any;
    validateField: (field: string) => void;
  };
  const {
    displayFormat = 'dd.MM.yyyy HH:mm',
    serverFormat = 't',
    placeholder,
    showTime = true,
    timeZone,
    timeZoneOffset,
    onChange = noopOnChange,
    minusOneSecond,
    shouldCloseOnSelect = true,
    minDate,
    maxDate,
    ...rest
  } = props;

  const [inputValue, setInputValue] = useState(null);
  // если напрямую вызывать changeFieldValue(inputValue) в onComplete,
  // то inputValue будет иметь предыдущее значение ввода
  // @see https://reactjs.org/docs/hooks-faq.html#why-am-i-seeing-stale-props-or-state-inside-my-function
  const latestInputValue = useLatest(inputValue);

  // признак того, что inputValue проинициализировано начальным значением
  // это сделает его невосприимчивым к изменениям поля формы
  const [inputValueInitialized, setInputValueInitialized] = useState(false);
  const [error, setError] = useState(undefined);

  const validate = () => inputValue && (
    isValid(inputValue) ? null : 'Ошибка ввода даты'
  );

  const [field, meta, helper] = useField({ validate, ...props } as FieldHookConfig<any>);

  const required = rest.required || isRequiredField(validationSchema, field.name);

  const minusOneSecondFunc = (dataTimeValue: Date): Date => {
    return minusOneSecond && (format(dataTimeValue, 'HH:mm') === '00:00') ? addSeconds(addDays(dataTimeValue, 1), -1) : dataTimeValue;
  };

  const changeFieldValue = (val: any) => {
    let str = valueToDateString(minusOneSecondFunc(val), serverFormat, props);

    // "сохраним" тип значения, передаваемого на сервер
    if ((serverFormat === 't' || Number.isInteger(field.value)) && !isEmpty(str)) {
      str = parseInt(str, 10);
    }

    // undefined и null будем считать равнозначными,
    // чтоб не обновлять поле формы, когда оно еще не инициализировано,
    // а пользователь открыл календарь и закрыл его не выбирая дату
    if ((field.value || null) !== (str || null)) {
      helper.setValue(str, false);
      helper.setTouched(true, false);

      onChange({ target: { name: field.name, value: minusOneSecondFunc(val), rawValue: str } });
    }
  };

  const changeInputValue = (dateValue: any, dateFormat: string) => {
    // Если пришел null/undefined во время ручного ввода - игнорируем
    if ((dateValue === null || dateValue === undefined) && isManualInputRef.current) {
      return;
    }

    const date = valueToDateObject(dateValue, dateFormat, props);

    if (isEqual(inputValue, date)) {
      return;
    }

    if (!isValid(date)) {
      setInputValue(null);
      return;
    }

    setInputValue(date);

    if (!inputValueInitialized) {
      setInputValueInitialized(true);
    }
  };

  const handleManualInputChange = (maskedValue: string) => {
    // Устанавливаем флаг ручного ввода
    isManualInputRef.current = true;

    // Более надежная проверка на пустоту - учитываем только цифры
    const hasDigits = maskedValue && /\d/.test(maskedValue);

    if (!hasDigits) {
      setInputValue(null);
      changeFieldValue(null);
    } else if (maskedValue.length === displayFormat.length) {
      try {
        const momentDate = moment(maskedValue, displayFormat, true);
        if (momentDate.isValid()) {
          const date = momentDate.toDate();
          setInputValue(date);
          changeFieldValue(date);
        } else {
          const parsedDate = parse(maskedValue, displayFormat, new Date());
          if (isValid(parsedDate)) {
            setInputValue(parsedDate);
            changeFieldValue(parsedDate);
          }
        }
      } catch {
        // Ошибка парсинга даты — игнорируем, оставляем текущее значение
      }
    } else {
      // Частичный ввод - ничего не делаем
    }

    // Сбрасываем флаг после небольшой задержки
    setTimeout(() => {
      isManualInputRef.current = false;
    }, 100);
  };

  const handleApply = (event: any) => {
    event.preventDefault();
    changeFieldValue(inputValue ?? Date.now());
    picker.current?.setOpen(false);
  };

  const handleReset = (event: any) => {
    event.preventDefault();
    changeInputValue(field.value, serverFormat);
    picker.current?.setOpen(false);
  };

  const handleClickOutside = () => {
    changeFieldValue(inputValue);
  };

  useEffect(() => {
    validateField(field.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  // Модифицируем эффекты синхронизации
  useEffect(() => {
    if (!isManualInputRef.current) {
      changeInputValue(field.value, serverFormat);
    }
    // eslint-disable-next-line
  }, [field.value, serverFormat]);

  useEffect(() => {
    if (!isManualInputRef.current) {
      changeInputValue(field.value, serverFormat);
    }
    // eslint-disable-next-line
  }, [timeZone, timeZoneOffset]);

  useEffect(() => {
    if ((meta.touched && (field.value !== undefined)) || (submitCount > 0)) {
      setError(meta.error);
    } else {
      setError(null);
    }
  }, [field.value, meta.error, meta.touched, submitCount]);

  useImperativeHandle(ref, () => picker.current);

  const inputProps = {
    ...rest,
    controlId: `datetime-input-${ field.name.replace(/\W/g, '-') }`,
    maskPattern: displayFormat,
    picker: picker.current,
    onClear: () => {
      changeFieldValue(null);
    },
    onComplete: () => changeFieldValue(latestInputValue.current),
    onManualChange: handleManualInputChange,
  };

  // Легаси-формат popper.js v1 (flip/preventOverflow) — react-datepicker v8 ходит на
  // floating-ui и это значение игнорирует; сохранено 1:1 из .jsx-версии.
  const popperModifiers = {
    flip: {
      enabled: false,
    },
    preventOverflow: {
      enabled: true,
      escapeWithReference: false,
    },
  } as any;

  useEffect(() => {
    return () => {
      setInputValue(null);
    };
  }, []);

  const handleToday = (event: any) => {
    event.preventDefault();
    const today = new Date();

    // Обновляем внутреннее состояние и значение в форме
    changeInputValue(today, displayFormat);
    changeFieldValue(today);

    // Всегда закрываем календарь при нажатии на "Сегодня"
    picker.current?.setOpen(false);
  };

  return (
    <DateTimePicker
      locale='ru'
      ref={ picker }
      name={ field.name }
      customInput={ (
        <DateTimeMask
          { ...(inputProps as any) } /* value из inputProps перекрывается DatePicker'ом:
                                      cloneElement(customInput) инжектирует строковое значение */
          error={ error }
        />
      ) }
      dateFormat={ displayFormat }
      selected={ inputValue }
      openToDate={ inputValue }
      placeholderText={ placeholder }
      popperModifiers={ popperModifiers }
      dropdownMode='select'
      timeFormat='HH:mm'
      timeInputLabel=''
      wrapperClassName='datetime-picker-wrapper'
      disabled={ props.disabled || disabled }
      readOnly={ props.readOnly || readOnly }
      required={ required }
      showTimeInput={ showTime }
      showPopperArrow={ false }
      shouldCloseOnSelect={ shouldCloseOnSelect }
      onChange={ (date: any) => {
        changeInputValue(date, displayFormat);
        changeFieldValue(date);
      } }
      onClickOutside={ handleClickOutside }
      peekNextMonth
      adjustDateOnChange
      preventOpenOnFocus
      minDate={ minDate ?? defaultMinDate }
      maxDate={ maxDate ?? defaultMaxDate() }
      showMonthDropdown
      showYearDropdown
      { ...(rest as any) } /* DatePickerProps v8 — юнион (selectsRange|selectsMultiple);
                                    спред широкого rest на юнион не тайпчекается, runtime 1:1 */
      isClearable={ false }
    >
      <div>
        <ButtonGroup className='p-1 btn-block'>
          <Button variant='outline-secondary' text='Сегодня' onClick={ handleToday } />
          { !shouldCloseOnSelect && (
            <>
              <Button variant='outline-primary' text='Установить' onClick={ handleApply } />
              <Button variant='outline-secondary' icon='close' onClick={ handleReset } />
            </>
          ) }
        </ButtonGroup>
      </div>
    </DateTimePicker>
  );
});

DateTimeInput.displayName = 'DateTimeInput';

export default DateTimeInput;
